/**
 * HANA Widget SDK — Entry Point
 *
 * Registers the <hana-chatbot> custom element and exposes
 * the window.HanaWidget global API for programmatic control.
 *
 * Embed via script tag:
 *   <script src="https://cdn.example.com/hana-widget.js"></script>
 *   <hana-chatbot entity-id="eec" api-endpoint="https://api.example.com"></hana-chatbot>
 *
 * Or via JavaScript:
 *   HanaWidget.init({ entityId: 'eec', apiEndpoint: '...', branding: {...} })
 */

import type { WidgetConfig, HanaWidgetAPI } from "./types";
import { HanaChatbotElement } from "./HanaChatbotElement";

const WIDGET_VERSION = "0.1.0";
const ELEMENT_TAG = "hana-chatbot";

/** Track the active element instance for the global API */
let activeElement: HanaChatbotElement | null = null;

/**
 * Register the custom element if not already registered.
 */
function registerElement(): void {
  if (!customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, HanaChatbotElement);
  }
}

/**
 * Initialize the widget programmatically.
 * Creates a <hana-chatbot> element, appends it to <body>,
 * and configures it with the provided config.
 */
function init(config: WidgetConfig): void {
  registerElement();

  // Destroy any existing instance
  if (activeElement) {
    destroy();
  }

  // Create and mount the element
  activeElement = document.createElement(ELEMENT_TAG) as HanaChatbotElement;
  document.body.appendChild(activeElement);

  // Initialize with the config object
  activeElement.initFromConfig(config);
}

/**
 * Open the chat panel.
 */
function open(): void {
  activeElement?.open();
}

/**
 * Close the chat panel.
 */
function close(): void {
  activeElement?.close();
}

/**
 * Destroy the widget and remove it from the DOM.
 */
function destroy(): void {
  if (activeElement) {
    activeElement.remove();
    activeElement = null;
  }
}

/** Global API object */
const HanaWidget: HanaWidgetAPI = {
  init,
  open,
  close,
  destroy,
  version: WIDGET_VERSION,
};

// Register the custom element on load
registerElement();

// Expose the global API
if (typeof window !== "undefined") {
  window.HanaWidget = HanaWidget;

  // Auto-initialize from script tag data attributes:
  //   <script src="hana-widget.js" data-entity="eec" data-api="https://..."></script>
  autoInitFromScriptTag();
}

/**
 * Detect data-entity and data-api on the <script> tag that loaded this bundle
 * and auto-initialize the widget. Defers to DOMContentLoaded if the DOM
 * isn't ready yet.
 */
function autoInitFromScriptTag(): void {
  const scriptTag = detectScriptTag();
  if (!scriptTag) return;

  const entityId = scriptTag.getAttribute("data-entity");
  const apiEndpoint = scriptTag.getAttribute("data-api");

  if (!entityId || !apiEndpoint) return;

  const doInit = () => {
    try {
      // Create and mount the custom element with script tag attributes
      const el = document.createElement(ELEMENT_TAG) as HanaChatbotElement;
      el.setAttribute("entity-id", entityId);
      el.setAttribute("api-endpoint", apiEndpoint);

      // Forward optional attributes
      const position = scriptTag.getAttribute("data-position");
      if (position) el.setAttribute("position", position);

      // REQ-D-046 — shell-variant attributes flow through the custom
      // element. Validation happens inside HanaChatbotElement, so an
      // unknown enum value here NEVER aborts the auto-init.
      const uiMode = scriptTag.getAttribute("data-ui-mode");
      if (uiMode) el.setAttribute("ui-mode", uiMode);
      const defaultSize = scriptTag.getAttribute("data-default-size");
      if (defaultSize) el.setAttribute("default-size", defaultSize);
      const allowExpand = scriptTag.getAttribute("data-allow-expand");
      if (allowExpand) el.setAttribute("allow-expand", allowExpand);
      const triggerCtaSelector = scriptTag.getAttribute(
        "data-trigger-cta-selector",
      );
      if (triggerCtaSelector) {
        el.setAttribute("trigger-cta-selector", triggerCtaSelector);
      }

      // Optional per-embed launcher mark. Validated in ToggleButton via
      // safeImageUrl; falls back to the built-in glyph when absent.
      const launcherIcon = scriptTag.getAttribute("data-launcher-icon");
      if (launcherIcon) el.setAttribute("launcher-icon", launcherIcon);

      document.body.appendChild(el);
      activeElement = el;
    } catch (err) {
      // Graceful failure: no console errors polluting the host site
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[HANA Widget] Auto-initialization failed:", err);
      }
    }
  };

  // Wait for DOM if not ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", doInit, { once: true });
  } else {
    doInit();
  }
}

/**
 * Find the <script> tag that loaded this bundle.
 * Uses document.currentScript (set during synchronous execution)
 * or falls back to searching for a script with data-entity.
 */
function detectScriptTag(): HTMLScriptElement | null {
  // document.currentScript works during initial (synchronous) execution
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }

  // Fallback: find a script with data-entity pointing to this bundle
  const scripts = document.querySelectorAll("script[data-entity][src]");
  for (const s of scripts) {
    if (s instanceof HTMLScriptElement && s.src.includes("hana-widget")) {
      return s;
    }
  }

  return null;
}

// Re-export types and components for library consumers
export { HanaChatbotElement } from "./HanaChatbotElement";
export { ChatWidget } from "./components/ChatWidget";
export {
  resolveConfig,
  ENTITY_PRESETS,
  SYSTEM_FONT_STACK,
  OUTFIT_FONT_STACK,
} from "./config";
export { ConsentPrompt } from "./components/ConsentPrompt";
export { FeedbackCapture } from "./components/FeedbackCapture";
export { InlineForm } from "./components/InlineForm";
export {
  TextField,
  PhoneField,
  DateField,
  DropdownField,
  TextareaField,
  NRICField,
} from "./components/form";
export { SuggestedActions } from "./components/SuggestedActions";
export {
  TextBubble,
  ListSelection,
  PackageCard,
  TypingIndicator,
  SystemMessage,
  createStatusIndicator,
  parseMarkdown,
  formatTimestamp,
  shouldShowTimestamp,
  createTimestampElement,
} from "./components/messages";
export { injectOutfitFont, requiresOutfitFont } from "./styles/fonts";
export type {
  WidgetConfig,
  ResolvedWidgetConfig,
  EntityBranding,
  TypographyScale,
  TypographyConfig,
  WidgetState,
  ViewportMode,
  HanaWidgetAPI,
  ConsentStatus,
  ConsentEvent,
  ConsentRequestMessage,
  ConsentCallbacks,
  SuggestedAction,
  SuggestedActionsPayload,
  SuggestedActionAnalyticsEvent,
  SuggestedActionAnalyticsType,
  FeedbackRating,
  FeedbackStage,
  FeedbackEvent,
  FeedbackRequestMessage,
  FeedbackCallbacks,
  FormFieldType,
  FormFieldDefinition,
  FormDropdownOption,
  FormMessage,
  FormSubmitEvent,
  FormCallbacks,
  FormStatus,
  FieldValidationError,
  MessageType,
  MessageSender,
  MessageStatus,
  ListItem,
  CardData,
  ChatMessage,
  MessageCallbacks,
} from "./types";

export { HanaWidget };
